import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import {
  catchError,
  NEVER,
  Observable,
  of,
  ReplaySubject,
  switchMap
} from 'rxjs';
import { SESSION_STORAGE_KEYS } from '../constants/storage.const';
import { getTenantFromUrl } from '../shared/util/helpers/url.helper';

/** How long to wait for Auth0 to issue an access token before giving up. */
export const TOKEN_TIMEOUT_MS = 10000;

/**
 * A second reauthentication within this long after a login redirect shows the
 * session-expired page instead of redirecting again.
 */
export const LOGIN_REDIRECT_RETRY_WINDOW_MS = 5 * 60 * 1000;

/**
 * Sends a user whose Auth0 session can no longer issue an access token
 * (expired, revoked or rotated refresh token, or no answer from Auth0) back
 * through login, at startup and mid-session. The cached id token keeps
 * `isAuthenticated$` true, so without this the app would keep calling the API
 * with no valid token.
 */
@Injectable({ providedIn: 'root' })
export class ReauthService {
  private readonly auth0Service = inject(Auth0Service);
  private readonly router = inject(Router);

  private result$: Observable<null> | null = null;

  /** True once reauthentication has started; the page is about to leave. */
  readonly isReauthenticating = signal(false);

  /**
   * Starts reauthentication, at most once per page load, so a burst of failed
   * requests redirects once rather than tripping the retry window.
   *
   * Redirects to login, returning to the current path afterwards. A second
   * failure within the retry window, or a redirect that cannot start, shows
   * the session-expired page instead, so a token that still cannot be issued
   * after a fresh login does not loop.
   *
   * The returned observable never emits while the login redirect is under
   * way, and emits `null` once the session-expired page is shown.
   */
  reauthenticate(): Observable<null> {
    this.result$ ??= this.start();
    return this.result$;
  }

  /**
   * Ends the retry window once the API has accepted the session. A token alone
   * is not enough: when the API rejects every token, clearing the window on
   * each fresh token would redirect to login in a loop.
   */
  clearRetryWindow(): void {
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.LOGIN_REDIRECTED_AT);
  }

  private start(): Observable<null> {
    this.isReauthenticating.set(true);

    const redirectedAt = Number(
      sessionStorage.getItem(SESSION_STORAGE_KEYS.LOGIN_REDIRECTED_AT)
    );
    if (
      redirectedAt &&
      Date.now() - redirectedAt < LOGIN_REDIRECT_RETRY_WINDOW_MS
    ) {
      return this.showSessionExpired();
    }

    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.LOGIN_REDIRECTED_AT,
      Date.now().toString()
    );
    // Subscribed here so the redirect starts even when the caller only fails
    // its request; the subject replays the outcome to the startup chain.
    const result$ = new ReplaySubject<null>(1);
    this.auth0Service
      .loginWithRedirect({ appState: { target: window.location.pathname } })
      .pipe(
        switchMap(() => NEVER),
        catchError(() => this.showSessionExpired())
      )
      .subscribe(result$);
    return result$.asObservable();
  }

  private showSessionExpired(): Observable<null> {
    void this.router.navigate(['error'], {
      queryParams: {
        detail: 'TOAST.SESSION_EXPIRED',
        tenantId: getTenantFromUrl() ?? ''
      }
    });
    return of(null);
  }
}
